/**
 * KorOS Build API client.
 *
 * Everything the backend contract requires lives here so no screen has to
 * remember it:
 *  - the `{data, meta}` / `{error:{code,message,details}}` envelope is unwrapped;
 *  - every mutation carries an `Idempotency-Key` (callers may pass a stable one
 *    so a retry is the same intent, not a second write);
 *  - `X-Build-Organization` scopes a request to an organisation when asked;
 *  - a 401 triggers exactly one refresh (single-flight), concurrent requests
 *    wait on it, and the original request is replayed with the same key;
 *  - reachability is judged by whether requests actually get through, not by
 *    `navigator.onLine` (which is wrong in both directions).
 *
 * The access token lives in memory only. The refresh token is an HttpOnly
 * cookie the browser sends on same-origin requests to `/api/v1/auth/*`.
 */
import {
  assertWritable,
  endViewAsLocally,
  isViewAs,
  noteReadOnlyAttempt,
  VIEW_AS_END_PATH,
  VIEW_AS_READ_ONLY,
} from '@/lib/auth/view-as';
import { reportPlanRefusal } from '@/lib/billing/refusals';
import { ApiError, NETWORK_ERROR } from './errors';
import type { AuthTokenResponse } from './types';

export const API_BASE = '/api/v1';
const REQUEST_TIMEOUT_MS = 20_000;
/** Boot waits on refresh, so a captive portal must not hold the app hostage. */
const REFRESH_TIMEOUT_MS = 8_000;

export type ApiMeta = Record<string, unknown>;
export type ApiEnvelope<T> = { data: T; meta: ApiMeta };

// ---------------------------------------------------------------------------
// Access token (memory only)
// ---------------------------------------------------------------------------

let accessToken: string | null = null;
let accessTokenExpiresAt = 0;
const tokenListeners = new Set<() => void>();

export function setAccessToken(token: string | null, expiresInSeconds?: number): void {
  accessToken = token;
  accessTokenExpiresAt = token && expiresInSeconds ? Date.now() + expiresInSeconds * 1000 : 0;
  if (token) refreshRefused = false;
  tokenListeners.forEach((listener) => listener());
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** True when a token is held and not within 30 s of expiring. */
export function hasUsableAccessToken(): boolean {
  if (!accessToken) return false;
  return accessTokenExpiresAt === 0 || accessTokenExpiresAt - Date.now() > 30_000;
}

export function subscribeToToken(listener: () => void): () => void {
  tokenListeners.add(listener);
  return () => tokenListeners.delete(listener);
}

// ---------------------------------------------------------------------------
// Reachability
// ---------------------------------------------------------------------------

let reachable = true;
const reachabilityListeners = new Set<() => void>();

function setReachable(next: boolean): void {
  if (reachable === next) return;
  reachable = next;
  reachabilityListeners.forEach((listener) => listener());
}

/** Whether the last real attempt to reach the server got through. */
export function isReachable(): boolean {
  return reachable;
}

/** The browser reported a network change: worth trying again (or not). */
export function hintReachability(online: boolean): void {
  setReachable(online);
}

export function subscribeToReachability(listener: () => void): () => void {
  reachabilityListeners.add(listener);
  return () => reachabilityListeners.delete(listener);
}

// ---------------------------------------------------------------------------
// Refresh (single-flight)
// ---------------------------------------------------------------------------

export type RefreshOutcome = 'ok' | 'refused' | 'unreachable';

let refreshPromise: Promise<RefreshOutcome> | null = null;
/**
 * The server already refused a refresh and nothing has signed in since.
 * Asking again would only spend rate-limit budget on an answer we have.
 * Only a refusal counts; network failures stay retryable.
 */
let refreshRefused = false;

let onSessionLost: (() => void) | null = null;
let onTokenRefreshed: ((response: AuthTokenResponse) => void) | null = null;

/** Registered by the session module (avoids an import cycle). */
export function setSessionHooks(hooks: {
  onSessionLost: () => void;
  onTokenRefreshed: (response: AuthTokenResponse) => void;
}): void {
  onSessionLost = hooks.onSessionLost;
  onTokenRefreshed = hooks.onTokenRefreshed;
}

export function refreshAccessToken(): Promise<RefreshOutcome> {
  // One refresh at a time: refresh tokens rotate, and parallel refreshes look
  // like token reuse to the server, which revokes the whole session.
  if (refreshPromise) return refreshPromise;
  if (refreshRefused) return Promise.resolve('refused');
  if (isViewAs()) {
    // A staff "view as" session never refreshes: the cookie here is the staff member's
    // own session, never the person's. A 401 means the view-as session ended.
    endViewAsLocally('ended');
    return Promise.resolve('refused');
  }

  refreshPromise = (async (): Promise<RefreshOutcome> => {
    try {
      const init: RequestInit = {
        method: 'POST',
        credentials: 'include',
        headers: { accept: 'application/json', 'content-type': 'application/json' },
        body: '{}',
      };
      const signal = withTimeout(undefined, REFRESH_TIMEOUT_MS);
      if (signal) init.signal = signal;
      const response = await fetch(buildUrl('/auth/refresh'), init);
      setReachable(true);
      if (response.status === 401 || response.status === 403) {
        refreshRefused = true;
        setAccessToken(null);
        onSessionLost?.();
        return 'refused';
      }
      if (!response.ok) return 'unreachable';
      const payload = (await response.json()) as ApiEnvelope<AuthTokenResponse>;
      setAccessToken(payload.data.access_token, payload.data.expires_in);
      onTokenRefreshed?.(payload.data);
      return 'ok';
    } catch {
      // Never reached the server. Not a lost session: queued work waits.
      setReachable(false);
      return 'unreachable';
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export type RequestOptions = {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Reuse a key to make a retry idempotent; omitted → a fresh one per call. */
  idempotencyKey?: string;
  /** Sends `X-Build-Organization` for org-scoped endpoints. */
  organizationId?: string | null;
  signal?: AbortSignal;
  /** Skip the refresh-and-retry dance (used by auth endpoints themselves). */
  skipAuthRetry?: boolean;
  headers?: Record<string, string>;
};

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === '') continue;
    url.searchParams.set(key, String(value));
  }
  return url.toString();
}

function withTimeout(
  signal: AbortSignal | undefined,
  ms: number = REQUEST_TIMEOUT_MS,
): AbortSignal | undefined {
  if (typeof AbortSignal === 'undefined' || typeof AbortSignal.timeout !== 'function') {
    return signal;
  }
  const timeout = AbortSignal.timeout(ms);
  if (!signal) return timeout;
  return typeof AbortSignal.any === 'function' ? AbortSignal.any([signal, timeout]) : signal;
}

export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<ApiEnvelope<T>> {
  const method = (options.method ?? 'GET').toUpperCase();
  // Viewing as someone (staff, read-only): nothing that changes anything leaves the device.
  if (MUTATING.has(method) && path !== VIEW_AS_END_PATH) assertWritable();
  const headers: Record<string, string> = { accept: 'application/json', ...options.headers };

  // A FormData body (file upload) sets its own multipart boundary.
  const multipart = typeof FormData !== 'undefined' && options.body instanceof FormData;
  if (options.body !== undefined && !multipart) headers['content-type'] = 'application/json';
  if (accessToken) headers['authorization'] = `Bearer ${accessToken}`;
  if (options.organizationId) headers['x-build-organization'] = options.organizationId;
  const idempotencyKey = MUTATING.has(method)
    ? (options.idempotencyKey ?? crypto.randomUUID())
    : undefined;
  if (idempotencyKey) headers['idempotency-key'] = idempotencyKey;

  const sentWith = accessToken;
  const init: RequestInit = { method, headers, credentials: 'include' };
  const signal = withTimeout(options.signal);
  if (signal) init.signal = signal;
  if (options.body !== undefined) {
    init.body = multipart ? (options.body as FormData) : JSON.stringify(options.body);
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), init);
  } catch (error) {
    if (options.signal?.aborted) throw error;
    setReachable(false);
    throw new ApiError({
      code: NETWORK_ERROR,
      message: 'Could not reach KorOS Build. Check your connection.',
      status: 0,
    });
  }
  // Any HTTP response at all proves the network works.
  setReachable(true);

  if (response.status === 401 && !options.skipAuthRetry) {
    const retry: RequestOptions = { ...options, skipAuthRetry: true };
    if (idempotencyKey) retry.idempotencyKey = idempotencyKey;
    // The token moved while this request was in flight: retry with the new one
    // instead of rotating again.
    if (accessToken && accessToken !== sentWith) return request<T>(path, retry);
    const outcome = await refreshAccessToken();
    if (outcome === 'ok') return request<T>(path, retry);
    if (outcome === 'unreachable') {
      throw new ApiError({
        code: NETWORK_ERROR,
        message: 'Could not reach KorOS Build. Check your connection.',
        status: 0,
      });
    }
  }

  if (response.status === 204) return { data: undefined as T, meta: {} };

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const error = (payload as { error?: Record<string, unknown> } | null)?.error ?? {};
    const failure = new ApiError({
      code: typeof error['code'] === 'string' ? error['code'] : `HTTP_${response.status}`,
      message: typeof error['message'] === 'string' ? error['message'] : response.statusText,
      status: response.status,
      details: (error['details'] as Record<string, unknown> | undefined) ?? {},
      requestId: response.headers.get('x-request-id'),
      retryable: typeof error['retryable'] === 'boolean' ? error['retryable'] : null,
    });
    // A change refused by Build's plans: the shell offers the plan page.
    if (MUTATING.has(method)) reportPlanRefusal(failure);
    if (failure.code === VIEW_AS_READ_ONLY) noteReadOnlyAttempt();
    throw failure;
  }

  const envelope = payload as Partial<ApiEnvelope<T>> | null;
  return { data: envelope?.data as T, meta: envelope?.meta ?? {} };
}

export type DownloadedFile = { blob: Blob; filename: string };

function filenameFrom(header: string | null, fallback: string): string {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header ?? '');
  if (!match?.[1]) return fallback;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

/**
 * Download a file the API renders (exports). Same auth and refresh rules as
 * `request`, but the answer is a Blob, not an envelope; an error still
 * arrives as the JSON envelope and is raised as an ApiError.
 */
export async function download(
  path: string,
  options: Pick<RequestOptions, 'query' | 'signal'> & { fallbackName?: string } = {},
  retried = false,
): Promise<DownloadedFile> {
  const headers: Record<string, string> = { accept: '*/*' };
  if (accessToken) headers['authorization'] = `Bearer ${accessToken}`;
  const sentWith = accessToken;
  const init: RequestInit = { method: 'GET', headers, credentials: 'include' };
  const signal = withTimeout(options.signal, 60_000);
  if (signal) init.signal = signal;
  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), init);
  } catch (error) {
    if (options.signal?.aborted) throw error;
    setReachable(false);
    throw new ApiError({
      code: NETWORK_ERROR,
      message: 'Could not reach KorOS Build. Check your connection.',
      status: 0,
    });
  }
  setReachable(true);
  if (response.status === 401 && !retried) {
    if (accessToken && accessToken !== sentWith) return download(path, options, true);
    const outcome = await refreshAccessToken();
    if (outcome === 'ok') return download(path, options, true);
  }
  if (!response.ok) {
    let payload: { error?: Record<string, unknown> } | null;
    try {
      payload = (await response.json()) as { error?: Record<string, unknown> };
    } catch {
      payload = null;
    }
    const error = payload?.error ?? {};
    throw new ApiError({
      code: typeof error['code'] === 'string' ? error['code'] : `HTTP_${response.status}`,
      message: typeof error['message'] === 'string' ? error['message'] : response.statusText,
      status: response.status,
      details: (error['details'] as Record<string, unknown> | undefined) ?? {},
      requestId: response.headers.get('x-request-id'),
    });
  }
  return {
    blob: await response.blob(),
    filename: filenameFrom(
      response.headers.get('content-disposition'),
      options.fallbackName ?? 'download',
    ),
  };
}

export const api = {
  get: <T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};

/** Test helper: forget all module state. */
export function resetClientForTests(): void {
  accessToken = null;
  accessTokenExpiresAt = 0;
  refreshPromise = null;
  refreshRefused = false;
  reachable = true;
}
